import clsx from 'clsx'
import type { DeploymentStatus } from '@/types'
import styles from './StatusBadge.module.css'

interface StatusBadgeProps {
  status: DeploymentStatus
  className?: string
}

const STATUS_LABELS: Record<DeploymentStatus, string> = {
  pending: 'Pending',
  validating: 'Validating',
  provisioning_network: 'Provisioning Network',
  provisioning_infra: 'Provisioning Infra',
  planning_infra: 'Planning Infrastructure',
  awaiting_plan_approval: 'Awaiting Plan Approval',
  applying_infra: 'Applying Infrastructure',
  verifying_server: 'Verifying Server',
  infra_ready: 'Infrastructure Ready',
  deploying_app: 'Deploying App',
  configuring: 'Configuring',
  configuring_haproxy: 'Configuring HAProxy',
  configuring_cloudflare: 'Configuring Cloudflare',
  post_deploy_config: 'Post-Deploy Config',
  configuring_integrations: 'Integrations',
  switching_traffic: 'Switching Traffic',
  running_health_checks: 'Health Checks',
  completed: 'Completed',
  failed: 'Failed',
  rolled_back: 'Rolled Back',
  cancelled: 'Cancelled',
  destroying: 'Destroying',
  destroyed: 'Destroyed',
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const label = STATUS_LABELS[status] ?? status

  const variantClass = (() => {
    switch (status) {
      case 'pending':
      case 'validating':
        return styles.muted
      case 'provisioning_network':
      case 'provisioning_infra':
      case 'planning_infra':
      case 'applying_infra':
      case 'verifying_server':
      case 'deploying_app':
      case 'configuring':
      case 'configuring_haproxy':
      case 'configuring_cloudflare':
      case 'post_deploy_config':
      case 'configuring_integrations':
      case 'switching_traffic':
      case 'running_health_checks':
        return styles.running
      case 'completed':
      case 'infra_ready':
        return styles.success
      case 'failed':
      case 'rolled_back':
        return styles.error
      case 'cancelled':
      case 'awaiting_plan_approval':
      case 'destroying':
        return styles.warning
      case 'destroyed':
        return styles.muted
      default:
        return styles.muted
    }
  })()

  const isPulsing =
    status === 'provisioning_network' ||
    status === 'provisioning_infra' ||
    status === 'planning_infra' ||
    status === 'applying_infra' ||
    status === 'verifying_server' ||
    status === 'deploying_app' ||
    status === 'configuring' ||
    status === 'configuring_haproxy' ||
    status === 'configuring_cloudflare' ||
    status === 'post_deploy_config' ||
    status === 'configuring_integrations' ||
    status === 'switching_traffic' ||
    status === 'running_health_checks' ||
    status === 'destroying'

  return (
    <span className={clsx(styles.badge, variantClass, className)}>
      {isPulsing && <span className={styles.pulse} />}
      {label}
    </span>
  )
}
